<template>
	<div class="user-management">
		<div class="container">
			<!-- Header -->
			<div class="page-header flex-center">
				<h1 class="page-title">Marketing Kit</h1>
				<div class="page-header-right text-right ml-a">
					<!-- Date Filter -->
					<daterangepicker :presets="filterDatePresets" :start="since" :end="until" @selected="setFilterDate"
						ref="datePicker">
					</daterangepicker>
				</div>
			</div>
			<div class="page-content">
				<!-- Table Menu -->
				<div class="table-list-menu">
					<div class="row">
						<div class="col-sm-6">
							<div class="table-actions flex-center" v-if="isAdmin">
								<button class="btn btn-primary btn-sm" @click="showFormModal('create')">
									<i class="material-icons">add</i><span>Add New</span>
								</button>
								<button class="btn btn-light btn-sm btn-icon-danger" :class="{ 'is-loading': isDeleting }"
									:disabled="selectedItems.length === 0" @click="confirmBulkAction('delete')">
									<i class="material-icons">delete</i>
									<span>Delete</span>
								</button>
								<button class="btn btn-light btn-sm btn-icon-danger" :class="{ 'is-loading': isDeleting }"
									:disabled="selectedItems.length === 0" @click="confirmBulkAction('restore')">
									<i class="material-icons">restore_from_trash</i>
									<span>Restore</span>
								</button>
								<!-- Selected Label -->
								<div class="btn btn-plain btn-sm disabled" v-if="selectedItems.length > 0">
									<span><b>{{ selectedItems.length }}</b> Selected Marketing Kit</span><span
										v-if="selectedItems.length > 1">s</span>
								</div>
							</div>
						</div>
						<div class="col-sm-6">
							<div class="table-filters d-flex align-items-center justify-content-end ml-a">
								<!-- Clear Filter -->
								<button type="button" class="btn btn-plain btn-sm" @click="clearFilter" v-if="isFilterOn">
									{{ $t('Clear Filter') }}
								</button>
								<!-- Filter -->
								<button type="button" class="filter-toggle btn btn-light btn-sm btn-icon"
									@click="isShowFilter = true">
									<i class="material-icons mr-1">filter_list</i>Filter Marketing Kit
								</button>
								<!-- Search Keyword -->
								<input type="text" class="input form-control form-control-sm users-search-box"
									placeholder="Search Marketing Kit.." autocomplete="off" @input="onKeywordChange"
									v-model="keyword" />
							</div>
						</div>
					</div>
				</div>
				<!-- Marketing Kit List -->
				<div class="users-list table-responsive">
					<table class="users-list-table table table-primary table-striped">
						<thead>
							<tr>
								<th width="5%">
									<label class="input-checkbox-container">
										<input type="checkbox" class="input-checkbox" v-model="selectAll" />
										<span></span>
									</label>
								</th>
								<th width="10%" class="text-left">
									Product Name
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('title')"
									@click="changeSorting('title')">
									Content Title
								</th>
								<th width="10%" class="th-sort text-left" :class="sortClass('description')"
									@click="changeSorting('description')">
									Description
								</th>
								<th width="10%" class="th-sort text-left">
									Preview Image
								</th>
								<th width="5%" class="th-sort text-left" :class="sortClass('price')"
									@click="changeSorting('price')">
									Price
								</th>
								<th width="5%" class="th-sort text-left" :class="sortClass('stock')"
									@click="changeSorting('stock')">
									Stock
								</th>
								<th width="5%" class="th-sort text-left" :class="sortClass('type')"
									@click="changeSorting('type')">
									Type
								</th>
								<th width="5%" class="th-sort text-left" :class="sortClass('created_at')"
									@click="changeSorting('created_at')">
									Created At
								</th>
								<th width="5%" class="text-left">
									Action
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in items" :key="item._id">
								<td class="align-middle">
									<label class="input-checkbox-container">
										<input type="checkbox" class="input-checkbox" :value="item._id"
											v-model="selectedItems" />
										<span></span>
									</label>
								</td>
								<td>
									<div>{{ item.product[0] ? item.product[0].name : '' }}</div>
								</td>
								<td>
									<div>{{ item.title }}</div>
								</td>
								<td>
									<div>{{ item.description }}</div>
								</td>
								<td>
									<img class="preview-img" :src="getPreviewImage(item)">
								</td>
								<td>
									<div>{{ formatMoney(item.price) }}</div>
								</td>
								<td>
									<div>{{ item.stock }}</div>
								</td>
								<td>
									<div>{{ item.type }}</div>
								</td>
								<td>
									<div class="user__date" :title="formatDateWithTime(item.created_at)">
										{{ formatDate(item.created_at) }}
									</div>
								</td>
								<td>
									<div class="d-flex justify-content-around">
										<i class="material-icons icon-style text-primary" v-tooltip="'Edit'"
											@click="showFormModal('edit', item)">edit</i>
										<i class="material-icons icon-style text-warning" v-tooltip="'Restore'"
											v-if="item.is_deleted"
											@click="confirmAction('restore', item)">restore_from_trash</i>
										<i class="material-icons icon-style text-danger" v-tooltip="'Delete'" v-else
											@click="confirmAction('delete', item)">delete</i>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
					<div class="table-fetch-status" v-if="isFetching">
						<div class="is-loading large dark m-auto"></div>
					</div>
					<!-- Empty -->
					<div class="list-empty" v-if="items.length === 0 && isFetching === false">
						<i class="material-icons mr-2">error_outline</i>
						<span v-if="keyword === ''">There is no marketing kit yet.</span>
						<span v-else>No marketing kit was found</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Modals -->
		<div class="modals">
			<!-- Table Filter -->
			<modal class="table-filter-modal" size="medium" @close="isShowFilter = false" v-if="isShowFilter">
				<div slot="header">
					<h3><i class="material-icons">filter_list</i>Filter Marketing Kit</h3>
				</div>
				<div slot="body">
					<!-- Supplier -->
					<div class="form-group">
						<label><b>{{ $t('Products') }}</b></label>
						<multiselect v-model="selectedProducts" track-by="slug" placeholder="Masukkan Nama Product"
							label="name" :show-labels="false" :allow-empty="true" :multiple="true" :options="products"
							@search-change="fetchProducts">
							<span slot="noResult">{{ $t('No Product found') }}</span>
						</multiselect>
					</div>

					<!-- deleted -->
					<div class="form-group">
						<label for="">
							<b class="">
								Status Deleted
							</b>
						</label>
						<select class="form-control" v-model="deleted">
							<option value="all">All</option>
							<option value="1">Deleted</option>
							<option value="0">Active</option>
						</select>
					</div>
				</div>
				<div slot="footer">
					<button type="button" class="btn btn-light" @click="isShowFilter = false">{{ $t('Close') }}</button>
					<button type="button" class="btn btn-primary" @click="applyFilter">Apply</button>
				</div>
			</modal>

			<!-- Bulk Delete Confirm -->
			<modal class="coupon-editor-modal" size="medium" v-if="isShowBulkAction" @close="isShowBulkAction = false">
				<div slot="header">
					<h3><i class="material-icons">{{ actionTypeIcon }}</i>{{ actionTypeLabel }} Marketing Kit</h3>
				</div>
				<div slot="body">
					Are you sure want to {{ actionType }} {{ selectedItems.length }} marketing kit?
				</div>
				<div slot="footer">
					<button type="button" class="btn btn-light" @click="isShowBulkAction = false">
						{{ $t('Close') }}
					</button>
					<button type="button" :class="`btn btn-${actionTypeButton}`" @click="bulkDelete">
						{{ actionTypeLabel }}
					</button>
				</div>
			</modal>

			<!-- Delete Confirm -->
			<modal class="coupon-editor-modal" size="medium" v-if="isShowActionModal" @close="isShowActionModal = false">
				<div slot="header">
					<h3><i class="material-icons">{{ actionTypeIcon }}</i>{{ actionTypeLabel }} Marketing Kit</h3>
				</div>
				<div slot="body">
					Are you sure want to {{ actionType }} {{ title }}?
				</div>
				<div slot="footer">
					<button type="button" class="btn btn-light" @click="isShowActionModal = false">
						{{ $t('Close') }}
					</button>
					<button type="button" :class="`btn btn-${actionTypeButton}`" @click="deleteItem">
						{{ actionTypeLabel }}
					</button>
				</div>
			</modal>

			<!-- Add New Announcement -->
			<modal class="announcement-create-modal" size="medium" v-if="isShowFormModal" @close="closeFormModal">
				<div slot="header">
					<h3>
						<i class="material-icons">{{ formTypeIcon }}</i>
						{{ formTypeLabel }} Marketing Kit
					</h3>
				</div>
				<div slot="body">
					<div class="container-fluid">
						<div class="row">
							<div class="col-md-12">
								<div class="form-group">
									<label><b>Product</b></label>
									<multiselect v-model="selectedProduct" track-by="slug"
										placeholder="Masukkan Nama Product" label="name"
										:class="{ 'multiselect-invalid': errorMessages.selectedProduct }"
										:show-labels="false" :allow-empty="true" :multiple="false" :options="products"
										@search-change="fetchProducts" @select="onProductChange">
										<span slot="noResult">{{ $t('No Product found') }}</span>
									</multiselect>
									<small v-if="errorMessages.selectedProduct" class="text-danger">{{
										errorMessages.selectedProduct }}</small>
								</div>

								<div class="form-group">
									<label><b>Type</b></label>
									<select class="form-control" v-model="type">
										<option :value="type.key" v-for="(type, index) in contentTypes" :key="index">{{
											type.label }}</option>
									</select>
								</div>

								<div class="form-group">
									<label><b>Title</b></label>
									<input type="text" name="title" class="input form-control"
										placeholder="Masukkan Judul Marketing Kit"
										:class="{ 'is-invalid': errorMessages.title }" autocomplete="off" v-model="title" />
									<small v-if="errorMessages.title" class="invalid-feedback">{{ errorMessages.title
									}}</small>
								</div>

								<div class="form-group">
									<label><b>Description</b></label>
									<textarea type="text" class="input form-control"
										placeholder="Masukkan Deskripsi Marketing Kit"
										:class="{ 'is-invalid': errorMessages.description }" v-model="description">
									</textarea>
									<small v-if="errorMessages.description" class="invalid-feedback">{{
										errorMessages.description }}</small>
								</div>

								<div class="form-group" v-if="type === 'paid'">
									<label><b>Price</b></label>
									<input type="number" name="price" class="input form-control" placeholder="Price"
										:class="{ 'is-invalid': errorMessages.price }" @input="onPriceChange"
										@change="onPriceChanges" autocomplete="off" v-model="price" />
									<small v-if="errorMessages.price" class="invalid-feedback">{{ errorMessages.price
									}}</small>
								</div>

								<div class="form-group" v-if="type === 'paid'">
									<label><b>Stock</b></label>
									<input type="number" name="stock" class="input form-control" placeholder="Stock"
										:class="{ 'is-invalid': errorMessages.stock }" @input="onStockChange"
										@change="onStockChanges" autocomplete="off" v-model="stock" />
									<small v-if="errorMessages.stock" class="invalid-feedback">{{ errorMessages.stock
									}}</small>
								</div>

								<div class="form-group">
									<label><b>Marketing Kit File</b></label>
									<div v-if="marketingKitMode === 'download'">
										<button type="button" :class="{ 'is-loading': isDownloading }"
											@click="downloadMarketingKit" class="btn btn-primary btn-sm mr-2">Download
											Marketing Kit</button>
										<button type="button" class="btn btn-danger btn-sm"
											@click="changeMarketingKitForm('upload')">Ubah Marketing Kit</button>
									</div>
									<div v-else>
										<input type="file" class="form-control" @change="onSelectFile">
										<div v-if="errorMessages.file" class="text-danger">{{ errorMessages.file }}</div>
										<button type="button" v-if="selectedContentCode" class="btn btn-danger btn-sm mt-2"
											@click="changeMarketingKitForm('download')">Batalkan Ubah Marketing Kit</button>
									</div>
								</div>

								<div class="form-group">
									<label><b>Preview Image</b></label>
									<div class="row">
										<div class="col-md-6">
											<p class="pc-p">
												Format gambar .jpg .jpeg .png dan ukuran minimum 300 x 300px (Untuk gambar
												optimal gunakan ukuran minimum 700 x 700 px). Ukuran maksimum gambar adalah
												<b>2MB</b>.
											</p>
										</div>
										<div class="col-md-6">
											<div class="box__content">
												<div class="pc-images-list">
													<draggable draggable=".draggable" v-model="product.images"
														v-bind="dragOptions" @start="drag = true" @end="drag = false">
														<transition-group class="row small-gutter" type="transition"
															:name="!drag ? 'flip-list' : null">
															<div class="col-md-12" :class="{ draggable: image !== '' }"
																v-for="(image, index) in product.images" :key="index + 0">
																<div v-if="isLoading && image === ''"
																	class="pc-image-empty">
																	<div class="image-loading">
																		<div class="is-loading"></div>
																	</div>
																</div>
																<div v-else-if="image === ''"
																	class="pc-image-empty cursor-pointer"
																	@click="uploadImages(index)">
																	<div class="image-upload-placeholder">
																		<i class="fi fi-rr-picture image-placeholder"></i>
																		<div class="text-placeholder">
																			Foto {{ index + 1 }}
																		</div>
																	</div>
																</div>
																<div v-else class="pc-image" :class="{
																	'is-loading': image === '',
																}">
																	<div class="img"
																		:style="{ backgroundImage: `url('${image}')` }">
																	</div>
																	<i class="material-icons" v-tooltip="'Remove'"
																		@click="removeProductImages(index)">close</i>
																</div>
															</div>
														</transition-group>
													</draggable>
												</div>
												<div class="form-group buttons m-0 hidden">
													<input type="file" accept="image/*" class="input-file"
														ref="imageUploader" id="pc-product-images-file"
														@change="onProductImagesChange" />
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div slot="footer">
					<button type="button" class="btn btn-light" @click="closeFormModal">
						Cancel
					</button>
					<button type="button" class="btn btn-primary" :class="{ 'is-loading': isCreating }" @click="create">
						Submit
					</button>
				</div>
			</modal>
		</div>

		<!-- Notifications -->
		<notifications group="product_contents" position="bottom right" />
	</div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import Modal from '@/components/Modal.vue';
import Daterangepicker from '@/components/Daterangepicker.vue';
import {
	duplicateVar,
	getDateRange,
	getAxiosErrorMessage,
	formatMoney,
	downloadFile,
} from '@/lib/helper';
import { defaultProductImageOstock } from '@/lib/statics';
import productContentApi from '@/api/product-content';
import mediaApi from '@/api/media';
import adminApi from '@/api/admin';

export default {
	name: 'AdminProductContent',
	components: {
		Modal,
		Daterangepicker,
		draggable,
	},
	data() {
		return {
			items: [],
			selectedItems: [],
			selectedItem: null,
			keyword: '',
			limit: 20,
			sort_by: 'created_at',
			sort: 'desc',
			isAll: false,
			isFetching: false,
			isCreating: false,
			isDeleting: false,
			isDownloading: false,
			isShowBulkAction: false,
			isShowActionModal: false,
			isShowFormModal: false,
			isShowFilter: false,
			since: null,
			until: null,
			formType: 'create', // create, edit
			actionType: 'delete', // delete, restore
			marketingKitMode: 'upload', // upload, download
			products: [],
			productKeyword: '',
			selectedProducts: [],
			deleted: '0',
			selectedProduct: null,
			selectedProductId: null,
			selectedContentId: null,
			selectedContentCode: null,
			title: '',
			description: '',
			price: 0,
			stock: 0,
			contentTypes: [
				{ key: 'paid', label: 'Paid' },
				{ key: 'free', label: 'Free' },
			],
			type: 'paid',
			isLoading: false, // is uploading images
			drag: false,
			dragOptions: {
				animation: 100,
			},
			product: {
				images: [''],
			},
			productImages: [],
			productImagesLimit: 1,
			file: null,
			errorMessages: {
				selectedProduct: '',
				title: '',
				description: '',
				price: '',
				stock: '',
				type: '',
				file: '',
			},
		};
	},
	methods: {
		formatMoney,
		resetItems() {
			this.items = [];
			this.selectedItems = [];
			this.isAll = false;
			this.page = 1;
		},
		getFetchParams() {
			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
				since: this.since,
				until: this.until,
			};

			if (this.selectedProducts.length > 0) {
				const products = this.selectedProducts.map(el => el.product_id);
				params.product_ids = JSON.stringify(products);
			}

			const keyword = this.keyword.trim();
			if (keyword !== '') {
				params.keyword = keyword;
			}

			if (this.deleted && this.deleted === '1') {
				params.is_deleted = true;
			} else if (this.deleted && this.deleted === '0') {
				params.is_deleted = false;
			}

			return params;
		},
		fetchItems(reset = true) {
			this.isFetching = true;
			if (reset) {
				this.resetItems();
			}

			const params = this.getFetchParams();

			const callback = (response) => {
				this.isFetching = false;
				const items = response.data;
				this.isShowFilter = false;
				if (items.length > 0) {
					this.page++;
					this.items = this.items.concat(items);
				} else {
					this.isAll = true;
				}
			};

			const errorCallback = () => {
				this.isFetching = false;
			};

			productContentApi.getList(params, callback, errorCallback);
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchItems(false);
				}
			}
		},
		bulkDelete() {
			this.isDeleting = true;
			const ids = this.selectedItems.slice();
			const actionType = this.actionType;

			const callback = () => {
				this.isDeleting = false;
				this.fetchProductContents();
				this.$set(this, 'selectedItems', []);

				this.isShowBulkAction = false;
				this.$notify({
					group: 'product_contents',
					title: 'Marketing Kit',
					text: 'Marketing Kit deleted',
					type: 'success',
				});
			};
			const errorCallback = () => {
				this.isDeleting = false;
				this.isShowBulkAction = false;
			};
			productContentApi.bulkDelete(ids, actionType, callback, errorCallback);
		},
		changeSorting(key) {
			const isSame = this.sort_by === key;
			this.sort_by = key;
			const sort = isSame && this.sort === 'asc' ? 'desc' : 'asc';
			this.sort = sort;
			// Re-fetch
			this.fetchItems(true);
		},
		sortClass(key) {
			const sameKey = this.sort_by === key;
			const className = sameKey ? `th-sort-${this.sort}` : '';
			return className;
		},
		formatDate(date) {
			return this.moment(date).format('DD-MM-YYYY');
		},
		formatDateWithTime(date) {
			return this.moment(date).format('DD-MM-YYYY HH:mm:ss');
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const dateFormat = 'YYYY-MM-DD';
			const since = dayjs(filterDate.start).format(dateFormat);
			const until = dayjs(filterDate.end).format(dateFormat);
			this.since = since;
			this.until = until;
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchItems(true);
		},
		initFilterDate() {
			const dateRange = getDateRange();
			const hasSinceQuery = this.sinceQuery && this.sinceQuery !== '';
			const hasUntilQuery = this.untilQuery && this.untilQuery !== '';
			const sinceDate = hasSinceQuery
				? dayjs(this.sinceQuery)
				: dayjs(dateRange.start);
			const untilDate = hasUntilQuery
				? dayjs(this.untilQuery)
				: dayjs(dateRange.end);
			const filterDate = {
				start: sinceDate,
				end: untilDate,
			};
			this.filterDate = filterDate;

			const dateFormat = 'YYYY-MM-DD';
			this.since = sinceDate.format(dateFormat);
			this.until = untilDate.format(dateFormat);
		},
		fetchData() {
			if (this.isAdmin) {
				this.fetchItems();
			}
		},
		fetchProductContents() {
			this.fetchData();
		},
		resetForm() {
			this.products = [];
			this.selectedProduct = null;
			this.selectedProductId = null;
			this.selectedContentId = null;
			this.selectedContentCode = null;
			this.marketingKitMode = 'upload';
			this.title = '';
			this.description = '';
			this.price = 0;
			this.stock = 0;
			this.type = 'paid';
			this.productImages = [];
			this.product.images = [''];
			this.file = null;
		},
		setForm(content) {
			this.products = content.product;
			this.selectedProduct = content.product[0];
			this.selectedProductId = content.product_id;
			this.selectedContentId = content._id;
			this.selectedContentCode = content.content_code;
			if (this.selectedContentCode) {
				this.marketingKitMode = 'download';
			}
			this.title = content.title;
			this.description = content.description;
			this.price = content.price;
			this.stock = content.stock;
			this.type = content.type;
			this.product.images[0] = content.image;
		},
		changeMarketingKitForm(mode = 'upload') {
			this.marketingKitMode = mode;
		},
		showFormModal(type = 'create', content = null) {
			this.resetForm();
			this.resetErrorForm();
			if (type === 'edit') {
				this.setForm(content);
			}
			this.formType = type;
			this.isShowFormModal = true;
		},
		closeFormModal() {
			this.isShowFormModal = false;
			this.resetForm();
			this.resetErrorForm();
		},
		confirmAction(type = 'delete', content) {
			this.resetForm();
			this.resetErrorForm();
			this.title = content.title;
			this.selectedContentId = content._id;
			this.actionType = type;
			this.isShowActionModal = true;
		},
		confirmBulkAction(type = 'delete') {
			this.actionType = type;
			this.isShowBulkAction = true;
		},
		create() {
			this.validateAllForm();
			const isErrorExist = Object.values(this.errorMessages).find(val => val !== '');
			if (isErrorExist) {
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Failed',
					text: 'Form harus diisi.',
				});
				return;
			}

			this.isCreating = true;

			const title = this.title.trim();
			const description = this.description.trim();
			const productId = this.selectedProduct.product_id ? this.selectedProduct.product_id : this.selectedProduct?._id?.$oid;
			if (this.type === 'free') {
				this.price = 0;
				this.stock = 0;
			}

			const params = new FormData();
			if (this.file !== null) {
				params.append('marketing_kit', this.file);
			}
			params.append('product_id', productId);
			params.append('title', title);
			params.append('description', description);
			params.append('price', this.price);
			params.append('stock', this.stock);
			params.append('type', this.type);
			params.append('image', this.product.images[0]);

			const callback = (response) => {
				this.isCreating = false;
				this.resetForm();
				this.resetErrorForm();
				this.fetchProductContents();
				this.closeFormModal();

				this.$notify({
					group: 'product_contents',
					title: 'Marketing Kit',
					text: response.message,
					type: 'success',
				});
			};

			const errorCallback = (e) => {
				this.isCreating = false;
				const message = e.response.message;

				this.$notify({
					group: 'product_contents',
					title: 'Marketing Kit',
					text: message,
					type: 'error',
				});
			};

			if (this.formType === 'edit') {
				const id = this.selectedContentId;
				productContentApi.update(id, params, callback, errorCallback);
			} else {
				productContentApi.create(params, callback, errorCallback);
			}
		},
		deleteItem() {
			this.isDeleting = true;
			const id = this.selectedContentId;
			const actionType = this.actionType;
			const params = {
				action_type: actionType,
			};

			const callback = () => {
				this.isDeleting = false;
				this.isShowActionModal = false;
				this.fetchProductContents();
				this.$notify({
					group: 'product_contents',
					title: 'Marketing Kit',
					text: 'Marketing Kit deleted',
					type: 'success',
				});
			};

			const errorCallback = () => {
				this.isDeleting = false;
				this.isShowActionModal = false;
			};

			if (actionType === 'restore') {
				productContentApi.restore(id, params, callback, errorCallback);
			}
			if (actionType === 'delete') {
				productContentApi.delete(id, params, callback, errorCallback);
			}
		},
		uploadImages(index) {
			this.currentIndexUploadImage = index;
			const inputFile = this.$refs.imageUploader;
			inputFile.click();
		},
		onProductImagesChange(event) {
			this.isLoading = true;
			const maxUploadSize = 2; // MB
			const input = event.target;
			for (let i = 0; i < input.files.length; i++) {
				const image = input.files[i];

				// Check File Size
				let fileSize = image.size;
				fileSize /= 1048576;

				// Limit Images
				const isLimitReached =
					this.productImages.length === this.productImagesLimit;
				const isFileSizeTooLarge = fileSize > maxUploadSize;

				// Show Notice if size too large
				if (isFileSizeTooLarge) {
					const noticeMessage = `'${image.name}' size is too large (max 2MB)`;
					this.$notify({
						group: 'app',
						text: noticeMessage,
						type: 'warn',
					});
					this.isLoading = false;
					break;
				}

				if (isLimitReached) {
					const noticeMessage = 'Maksimum upload 1 foto produk';
					this.$notify({
						group: 'app',
						text: noticeMessage,
						type: 'warn',
					});
					this.isLoading = false;
					break;
				}

				this.productImages.push(image);

				const previewIndex = this.currentIndexUploadImage;

				this.$set(this.product.images, previewIndex, '');

				// Load the image file into a canvas element
				const img = new Image();
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');
				const reader = new FileReader();
				// eslint-disable-next-line no-loop-func
				reader.onload = () => {
					img.onload = () => {
						const size = Math.max(img.width, img.height);
						canvas.width = size;
						canvas.height = size;
						const offsetX = (size - img.width) / 2;
						const offsetY = (size - img.height) / 2;
						ctx.fillStyle = '#fff';
						ctx.fillRect(0, 0, size, size); // fill the canvas with white
						ctx.drawImage(img, offsetX, offsetY);

						// Convert the canvas to a blob and upload it to the server
						canvas.toBlob((blob) => {
							// Start Upload
							const uploadParams = new FormData();
							uploadParams.append('image', blob);

							const callback = (response) => {
								this.isLoading = false;
								const path = response.data;
								const url = `${this.mediaUrlPrefix}/${path}`;
								this.$set(this.product.images, previewIndex, url);
							};

							const errorCallback = () => {
								this.isLoading = false;
								// eslint-disable-next-line
								this.productImages.splice(previewIndex, 1);
								this.product.images.splice(previewIndex, 1);
							};

							// Upload Image
							mediaApi.uploadImagePublic(uploadParams, callback, errorCallback);
						}, image.type, 0.9);
					};
					img.src = reader.result;
				};
				reader.readAsDataURL(image);
			}
			event.target.files = null;
			event.target.value = '';
		},
		removeProductImages(index) {
			this.productImages.splice(index, 1);
			this.product.images.splice(index, 1, '');
		},
		onSelectFile(event) {
			const files = event.target.files;
			if (files.length > 0) {
				const file = files[0];
				this.file = file;
			}
			this.fileValidation();
		},
		onProductChange(product) {
			this.selectedProductId = product.product_id;
		},
		fetchProducts(query) {
			if (query.length >= 1) {
				this.dropshipperKeyword = query;
				this.getProducts();
			}
		},
		getProducts() {
			const params = {
				limit: 50,
				sort_by: 'created_at',
				sort: 'desc',
				page: 1,
				keyword: this.productKeyword,
			};

			const callback = (response) => {
				let products = response.data;

				if (products.length > 0) {
					products = products.map((data) => {
						const newObj = {};
						newObj.product_id = data._id;
						newObj.name = data.name;
						newObj.slug = data.slug;
						return newObj;
					});
				}

				this.products = products;
			};
			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);
				console.log(msg);
			};
			adminApi.getProducts(params, callback, errorCallback);
		},
		applyFilter() {
			this.fetchProductContents();
		},
		resetFilter() {
			this.selectedProducts = [];
			this.deleted = '0';
			this.keyword = '';
		},
		clearFilter() {
			this.$router.replace({ query: null });
			this.resetFilter();
			this.applyFilter();
		},
		onKeywordChange() {
			this.ooDelay(() => {
				if (this.isAdmin) {
					this.fetchItems();
				} else if (this.isSupport) {
					if (this.keyword.trim() === '') {
						this.resetItems();
					} else {
						this.fetchItems();
					}
				}
			}, 1000);
		},
		getPreviewImage(item) {
			return item.image ? item.image : defaultProductImageOstock;
		},
		parsePricing(price) {
			let parsedPrice = price.replace(/[^0-9]/g, '').trim();
			const parsedPriceLength = parsedPrice.length;
			if (parsedPriceLength > 1 && parsedPrice.charAt(0) === '0') {
				parsedPrice = parsedPrice.substring(1, parsedPriceLength);
			}
			return parsedPrice;
		},
		onPriceChange() {
			this.price = this.parsePricing(this.price);
		},
		onPriceChanges() {
			if (this.price === '') {
				this.price = 0;
			}
		},
		onStockChange() {
			this.stock = this.parsePricing(this.stock);
		},
		onStockChanges() {
			if (this.stock === '') {
				this.stock = 0;
			}
		},
		selectedProductValidation() {
			if (this.selectedProduct) {
				this.errorMessages.selectedProduct = '';
			} else {
				this.errorMessages.selectedProduct = 'Produk Wajib Dipilih';
			}
		},
		titleValidation() {
			if (this.title.length <= 0) {
				this.errorMessages.title = 'Judul Wajib Diisi';
			} else {
				this.errorMessages.title = '';
			}
		},
		descriptionValidation() {
			if (this.description.length <= 0) {
				this.errorMessages.description = 'Deskripsi Wajib Diisi';
			} else {
				this.errorMessages.description = '';
			}
		},
		priceValidation() {
			if ((this.type === 'paid' && parseInt(this.price) === 0) || this.price.length <= 0) {
				this.errorMessages.price = 'Harga Wajib Diisi';
			} else {
				this.errorMessages.price = '';
			}
		},
		stockValidation() {
			if (this.type === 'paid' && (parseInt(this.stock) === 0 || this.stock.length <= 0)) {
				this.errorMessages.stock = 'Stock Wajib Diisi';
			} else {
				this.errorMessages.stock = '';
			}
		},
		typeValidation() {
			if (this.type === '') {
				this.errorMessages.type = 'Type Wajib Dipilih';
			} else {
				this.errorMessages.type = '';
			}
		},
		fileValidation() {
			if (this.marketingKitMode === 'upload' && this.file === null) {
				this.errorMessages.file = 'File Wajib Diisi';
			} else {
				this.errorMessages.file = '';
			}
		},
		validateAllForm() {
			this.selectedProductValidation();
			this.titleValidation();
			this.descriptionValidation();
			this.priceValidation();
			this.stockValidation();
			this.typeValidation();
			this.fileValidation();
		},
		resetErrorForm() {
			this.errorMessages = {
				selectedProduct: '',
				title: '',
				description: '',
				price: '',
				stock: '',
				type: '',
				file: '',
			};
		},
		downloadMarketingKit() {
			this.isDownloading = true;
			const params = {
				content_code: this.selectedContentCode,
			};
			const callback = (response) => {
				this.isDownloading = false;
				const url = response.data;
				downloadFile(url);
			};
			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);
				console.log(msg);
				this.isDownloading = false;
				this.$notify({
					group: 'product_contents',
					title: 'Marketing Kit',
					text: msg,
					type: 'error',
				});
			};

			productContentApi.download(params, callback, errorCallback);
		},
	},
	computed: {
		...mapGetters({
			isAdmin: 'isAdmin',
			filterDatePresets: 'filterDatePresets',
		}),
		selectAll: {
			get() {
				return this.items
					? this.selectedItems.length === this.items.length
					: false;
			},
			set(value) {
				const selected = [];
				if (value) {
					this.items.forEach((item) => {
						selected.push(item._id);
					});
				}
				this.selectedItems = selected;
			},
		},
		sinceQuery() {
			const since = this.$route.query.since;
			return since;
		},
		untilQuery() {
			const until = this.$route.query.until;
			return until;
		},
		isFilterOn() {
			return this.keyword !== ''
				|| this.deleted !== '0'
				|| this.selectedProducts.length > 0;
		},
		actionTypeLabel() {
			return this.actionType === 'delete' ? 'Delete' : 'Restore';
		},
		actionTypeIcon() {
			return this.actionType === 'delete' ? 'delete' : 'restore_from_trash';
		},
		actionTypeButton() {
			return this.actionType === 'delete' ? 'danger' : 'warning';
		},
		formTypeLabel() {
			return this.formType === 'create' ? 'Create' : 'Edit';
		},
		formTypeIcon() {
			return this.formType === 'create' ? 'add' : 'edit';
		},
	},
	watch: {
		isAdmin() {
			this.fetchProductContents();
		},
		selectedProduct() {
			this.selectedProductValidation();
		},
		title() {
			this.titleValidation();
		},
		description() {
			this.descriptionValidation();
		},
		price() {
			this.priceValidation();
		},
		stock() {
			this.stockValidation();
		},
		type() {
			this.typeValidation();
		},
	},
	created() {
		this.initFilterDate();
		// Check Params & Fetch Data
		this.fetchProductContents();
		// On Scroll Bottom
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style scoped>
.icon-style {
	font-size: 20px;
	cursor: pointer;
}

.preview-img {
	max-width: 100px;
	max-height: 100px;
}

.multiselect-invalid {
	.multiselect__tags {
		border-color: #ed1c24;
	}
}
</style>
